/*
  Cuts a network round trip out of every cart update.

  Updating the drawer took two trips to the server: one to change the cart (/cart/add.js,
  /cart/change.js, /cart/update.js), then a second one to fetch the re-rendered drawer markup
  (/cart?section_id=api-cart-items). Nothing could be drawn until the second one landed — and
  the checkout button is hidden until then, since the theme only reveals the cart form once it
  knows the cart has items. On a phone that second trip is the difference between a drawer that
  feels instant and one that hesitates.

  The Ajax API can return rendered sections alongside the cart response, so this asks for the
  drawer markup in the same request and answers the theme's follow-up from what came back. The
  theme is not modified: it still fires both requests, the second one is simply served from the
  first one's payload instead of the network.

  It also stops identical /cart.js reads from overlapping. Several parts of the drawer refresh
  themselves on the same event, and each was opening its own request for the same bytes; while
  one is in flight the others now share it. Only genuinely concurrent reads are shared — nothing
  is cached, so no part of the page can be handed a cart older than the one it asked for.

  Anything unexpected — a body shape that cannot carry the extra field, a response without the
  section, a rejected request — falls straight through to the untouched native fetch.
*/
(() => {
  const SECTION = 'api-cart-items';

  // Long enough to cover the theme's own hand-off between the two requests, short enough that
  // markup from an earlier update can never answer a later one.
  const FRESH_MS = 3000;

  const nativeFetch = window.fetch;
  if (typeof nativeFetch !== 'function' || window.__cartSpeed) return;
  window.__cartSpeed = true;

  let pendingSection = null;
  let pendingSectionAt = 0;
  let pendingCart = null;
  let pendingCartAt = 0;
  // Bumped by every cart change, so a response that comes back out of order is dropped
  // rather than overwriting the newer cart it was overtaken by
  let version = 0;
  const inFlight = new Map();

  const urlOf = (input) => {
    if (typeof input === 'string') return input;
    if (input instanceof Request) return input.url;
    return String((input && input.url) || '');
  };

  const methodOf = (input, init) => {
    const method = (init && init.method) || (input instanceof Request ? input.method : 'GET');
    return String(method).toUpperCase();
  };

  // An app or a widget may talk to a cart endpoint that is not this shop's; only this shop's
  // own cart is ours to speak for
  const sameOrigin = (url) => {
    if (!url) return false;
    if (url.charAt(0) === '/' && url.charAt(1) !== '/') return true;

    try {
      return new URL(url, window.location.href).origin === window.location.origin;
    } catch (error) {
      return false;
    }
  };

  const isCartMutation = (url) => sameOrigin(url) && /\/cart\/(add|change|update)(\.js)?(\?|$)/.test(url);
  const isSectionRequest = (url) => sameOrigin(url) && url.indexOf(`section_id=${SECTION}`) !== -1;
  const isCartJson = (url) => sameOrigin(url) && /\/cart\.js(\?|$)/.test(url);

  /*
    A copy of the body carrying the section request. Copied rather than mutated: the caller
    still owns its own body and may reuse it, and a FormData quietly gaining a field would be
    a surprise on a retry.
  */
  const withSection = (body) => {
    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      if (body.has('sections')) return null;
      const copy = new FormData();
      body.forEach((value, key) => copy.append(key, value));
      copy.append('sections', SECTION);
      return copy;
    }

    if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
      if (body.has('sections')) return null;
      const copy = new URLSearchParams(body.toString());
      copy.set('sections', SECTION);
      return copy;
    }

    if (typeof body === 'string') {
      const trimmed = body.trim();

      if (trimmed.charAt(0) === '{') {
        try {
          const parsed = JSON.parse(trimmed);
          if (parsed === null || typeof parsed !== 'object' || parsed.sections) return null;
          parsed.sections = SECTION;
          return JSON.stringify(parsed);
        } catch (error) {
          return null;
        }
      }

      if (trimmed.indexOf('=') !== -1) {
        const params = new URLSearchParams(trimmed);
        if (params.has('sections')) return null;
        params.set('sections', SECTION);
        return params.toString();
      }
    }

    return null;
  };

  /*
    Read from a clone so the theme still gets an untouched, unread response.

    /cart/change.js and /cart/update.js answer with the whole cart, which is the very thing the
    blocks then go and read from /cart.js. That copy is kept for the read that follows — once,
    and only until the next cart change starts, so nothing can ever be handed a cart that has
    since moved on.
  */
  const rememberResponse = (response, mine) => {
    // Overtaken by a later change: this answer describes a cart that no longer exists
    if (mine !== version) return response;

    try {
      const reading = response.clone().json().catch(() => null);
      const keep = (value) => (mine === version ? value : null);

      pendingSection = reading.then((data) =>
        keep((data && data.sections && data.sections[SECTION]) || null)
      );
      pendingSectionAt = Date.now();

      pendingCart = reading.then((data) => {
        if (!data || typeof data.item_count !== 'number') return null;
        const cart = Object.assign({}, data);
        delete cart.sections;
        return keep(cart);
      });
      pendingCartAt = Date.now();
    } catch (error) {
      pendingSection = null;
      pendingCart = null;
    }

    return response;
  };

  window.fetch = function (input, init) {
    const args = arguments;
    let url = '';
    let method = 'GET';

    try {
      url = urlOf(input);
      method = methodOf(input, init);
    } catch (error) {
      return nativeFetch.apply(this, args);
    }

    // 1. Ask for the drawer markup in the same request that changes the cart
    if (method === 'POST' && isCartMutation(url)) {
      // Whatever was kept from the previous change describes a cart that is about to change
      pendingSection = null;
      pendingCart = null;
      pendingSectionAt = 0;
      pendingCartAt = 0;

      const mine = ++version;
      const body = init && init.body != null ? withSection(init.body) : null;

      if (body !== null) {
        const patched = Object.assign({}, init, {body});
        return nativeFetch.call(this, input, patched).then((response) => rememberResponse(response, mine));
      }
    }

    // 2. Answer the theme's follow-up from what that request already brought back
    if (
      method === 'GET' &&
      isSectionRequest(url) &&
      pendingSection &&
      Date.now() - pendingSectionAt < FRESH_MS
    ) {
      const promised = pendingSection;
      const self = this;
      pendingSection = null;

      return promised.then((html) =>
        html
          ? new Response(html, {status: 200, headers: {'Content-Type': 'text/html; charset=utf-8'}})
          : nativeFetch.apply(self, args)
      );
    }

    // 3. Answer the first cart read after a change from what that change already returned
    if (method === 'GET' && isCartJson(url) && pendingCart && Date.now() - pendingCartAt < FRESH_MS) {
      const promised = pendingCart;
      const self = this;
      pendingCart = null;

      return promised.then((cart) =>
        cart
          ? new Response(JSON.stringify(cart), {
              status: 200,
              headers: {'Content-Type': 'application/json; charset=utf-8'},
            })
          : nativeFetch.apply(self, args)
      );
    }

    // 4. One cart read at a time, shared by whoever asks while it is in flight
    if (method === 'GET' && isCartJson(url)) {
      const running = inFlight.get(url);
      if (running) return running.then((response) => response.clone());

      const request = nativeFetch.apply(this, args);
      inFlight.set(url, request);

      const clear = () => {
        if (inFlight.get(url) === request) inFlight.delete(url);
      };
      request.then(clear, clear);

      return request.then((response) => response.clone());
    }

    return nativeFetch.apply(this, args);
  };
})();
